/**
 * `strategies` namespace — Korean translations.
 *
 * Mirrors `en/strategies.ts` exactly (compile-checked via `Record<StrategiesKey, string>`).
 * The status-vocabulary blocks follow the product glossary verbatim so the same enum
 * reads identically across strategies / orders / approvals: 부분 체결(partial), 거부됨(rejected),
 * 만료됨(expired), 거부(AI)(vetoed), 시그널 없음(no signal), 대기(pending). "DRY-RUN"/"Live" stay
 * English on the compact guard badges (see meta.tsx); prose uses 모의 실행 / 실거래. Concise
 * 명사형/합니다체 for chrome and descriptions.
 */
import type { StrategiesKey } from "../en/strategies";

export const strategies: Record<StrategiesKey, string> = {
  // ── list route ──
  listEyebrow: "자동화",
  listTitle: "전략",
  listDescription: "거래 전략과 각 전략의 상태, 전략별 모의 실행 여부를 확인합니다.",
  listLoadError: "전략을 불러오지 못했습니다.",
  filterAll: "전체",
  emptyFiltered: "이 필터에 해당하는 전략이 없습니다.",
  emptyTitle: "아직 전략이 없습니다.",
  emptyHint: "아래 프리셋을 설치하거나, 내보낸 전략을 가져오거나, Chat에서 직접 만들어 보세요.",

  // ── strategy status (canonical) ──
  statusDraft: "초안",
  statusActive: "활성",
  statusPaused: "일시중지",
  statusHalted: "중단됨",
  statusArchived: "보관됨",

  // ── strategy card ──
  tradesCount: "거래 {count}건",
  winRate: "승률 {rate}",
  noWinsYet: "아직 수익 거래 없음",
  noTradesYet: "아직 거래 없음",
  pnlTrendAria: "실현 손익 추세",

  // ── controls ──
  exportJson: "JSON 내보내기",
  exporting: "내보내는 중…",
  editInChat: "Chat에서 편집",
  liveOrdersLabel: "실거래 주문",
  dryRunSimulated: "시뮬레이션",
  liveOrdersActive: "실거래 중",
  dryRunActiveHint: "지금은 모의 실행 — 주문이 전송되지 않습니다.",
  liveOrdersActiveHint: "실제 주문이 전송됩니다. 고액 주문은 여전히 승인 단계를 거칩니다.",
  toggleLiveOrders: "실거래 주문 전환",
  lifecycleActivate: "활성화",
  lifecyclePause: "일시중지",
  lifecycleResume: "재개",
  lifecycleHalt: "중단",
  lifecycleReactivate: "재활성화",
  lifecycleArchive: "보관",
  lifecycleUnarchive: "복원",
  reactivateConfirm:
    "중단은 보통 리스크 위반 등 원인이 있어 발생합니다. 원인을 확인했다면 재활성화하세요.",
  deleteTitle: "전략 삭제",
  deleteHint: "이 전략을 영구적으로 제거합니다. 거래 내역은 보존됩니다.",
  deleteActiveHint: "삭제하려면 먼저 전략을 일시중지하거나 중단하세요.",
  deleteStrategy: "삭제",
  deleteConfirm: "삭제 확인",
  deleting: "삭제 중…",

  // ── preset gallery ──
  presetGalleryTitle: "프리셋 갤러리",
  presetGallerySubtitle: "검증된 전략을 모의 실행 초안으로 설치",
  presetsLoadError: "프리셋을 불러오지 못했습니다.",
  presetsEmpty: "번들된 프리셋이 없습니다.",
  presetInstallFailed: "설치에 실패했습니다.",
  presetInstalling: "설치 중…",
  presetInstall: "초안으로 설치",

  // ── import panel ──
  importOpen: "JSON 가져오기",
  importTitle: "전략 가져오기",
  importSubtitle: "전략 내보내기 파일을 붙여넣거나 업로드",
  importing: "가져오는 중…",
  importAsDraft: "초안으로 가져오기",
  chooseFile: "파일 선택…",
  importInvalidJson: "유효한 JSON이 아닙니다.",
  importInvalidEnvelope: "유효한 전략 내보내기 파일이 아닙니다.",
  importFailed: "가져오기에 실패했습니다.",

  // ── detail route ──
  strategyEyebrow: "전략",
  notFound: "찾을 수 없음",
  detailLoadError: "이 전략을 불러올 수 없습니다.",
  backToStrategies: "← 전략 목록으로",
  detailEyebrow: "전략 · {slug}",
  controlsTitle: "컨트롤",
  controlsSubtitle: "라이프사이클, 모의 실행 가드, 내보내기",
  metaAccount: "계좌",
  metaUnbound: "미연결",
  metaBudget: "예산",
  metaNextRun: "다음 실행",
  metaUpdated: "업데이트",
  priceTitle: "가격",
  primarySymbol: "주요 종목",
  noPrimarySymbol: "유니버스에서 주요 종목을 확인할 수 없습니다.",
  priceWithSymbol: "가격 · {symbol}",
  dailyCandles: "일봉",

  // ── account binding (detail route) ──
  accountBindingTitle: "계좌 연결",
  accountBindingSubtitle: "거래 계좌와 예산(선택)을 설정합니다.",
  tradingAccountLabel: "거래 계좌",
  budgetAmountLabel: "예산 (선택)",
  budgetCurrencyLabel: "통화",
  budgetAmountInvalid: "유효한 금액을 입력하세요 (예: 1000000).",
  saveAccountBinding: "저장",
  savingAccountBinding: "저장 중…",
  noAccountsBound: "계좌가 없습니다 — 아래에서 동기화하세요:",
  accountBindingLockedHint: "활성 상태에서는 변경할 수 없습니다 — 일시중지 후 변경하세요.",

  // ── decision-replay route ──
  runEyebrowSingle: "실행",
  decisionReplay: "의사결정 리플레이",
  runLoadError: "이 실행을 불러올 수 없습니다.",
  backToStrategy: "← 전략으로",
  runEyebrow: "실행 · {tick}",
  runsEyebrow: "실행 이력 · {slug}",
  decisionReplayDescription: "단계별 감사: 지표, 조건 추적, 리스크 검사, AI 판정, 주문 의도.",
  strategyAction: "← 전략",
  runSummary: "실행 요약",
  metaRoute: "경로",
  metaScheduled: "예약",
  metaStarted: "시작",
  metaFinished: "종료",

  // ── run state (canonical) ──
  runStateScheduled: "예약됨",
  runStateEvaluating: "평가 중",
  runStatePendingApproval: "승인 대기",
  runStatePendingAi: "AI 대기",
  runStateExecuting: "실행 중",

  // ── run outcome (canonical) ──
  noSignal: "시그널 없음",
  blockedRisk: "차단 · 리스크",
  filled: "체결",
  partialFill: "부분 체결",
  rejected: "거부됨",
  skipped: "건너뜀",
  vetoed: "거부(AI)",
  expired: "만료됨",
  outcomeError: "오류",
  pending: "대기",

  // ── execution mode / run route (canonical) ──
  modeAuto: "자동",
  modeApproval: "승인",
  modeAiAdvisor: "AI 어드바이저",

  // ── decision phase (canonical) ──
  phaseEvaluate: "평가",
  phaseRisk: "리스크",
  phaseRoute: "라우팅",
  phaseExecute: "실행",
};
